<template>
    <MultiviewEditableWidget
        class="editable-image-widget"
        :is-opened="isOpen"
        :read-only="readOnly"
        @input="toggleOpen"
    >
        <template #name>{{ editable.label }}</template>
        <template #controls-prefix>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>
        <template #controls>
            <Icon
                v-if="$auth.userCan($auth.Actions.CanManageMediaItems)"
                type="md-folder"
                size="18"
                @click.stop="smartPlacement"
            />
        </template>

        <template #list-view>
            <EditableFileListView
                :editable="editable"
                :groups-affecting-editable="groupsAffectingEditable"
                :is-opened="isOpen"
                @update="updateFileEditable"
            />
        </template>

        <template #grid-view>
            <EditableFileGridView
                :editable="editable"
                :groups-affecting-editable="groupsAffectingEditable"
                :is-opened="isOpen"
                @update="updateFileEditable"
            />
        </template>
    </MultiviewEditableWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, ref, type PropType } from "vue";
import type { Editable } from "hoxton-types";

import EditableFileGridView from "@/components/Campaign/Editables/EditableFileGridView.vue";
import EditableFileListView from "@/components/Campaign/Editables/EditableFileListView.vue";
import MultiviewEditableWidget from "@/components/Campaign/widgets/MultiviewEditableWidget.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useMasterTemplates } from "@/composables/useMasterTemplates";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useEditableFile } from "@/composables/useEditableFile";

import type { MediaItem } from "@/types/types";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";

export default defineComponent({
    name: "EditableImageWidget",

    components: {
        EditableRestrictionControl,
        MultiviewEditableWidget,
        EditableFileListView,
        EditableFileGridView
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");
        const groupsAffectingEditable = toRef(props, "groupsAffectingEditable");
        const type = ref(editable.value.type);

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { masterTemplateIds, campaignMasterTemplates } = useMasterTemplates();
        const { update } = useEditableMethods({ emit });
        const { updateFileEditable } = useEditableFile({ emit, editable, groupsAffectingEditable, type });

        return {
            isOpen,
            toggleOpen,
            masterTemplateIds,
            campaignMasterTemplates,
            update,
            updateFileEditable
        };
    },

    computed: {
        masterTemplateSizes() {
            return this.masterTemplateIds.reduce<Record<string, string>>(
                (acc, cur) =>
                    Object.assign(acc, {
                        [cur]: `${this.campaignMasterTemplates[cur].width}x${this.campaignMasterTemplates[cur].height}`
                    }),
                {}
            );
        }
    },

    methods: {
        async smartPlacement() {
            // 1. get file selection
            const selectedFiles = (await this.$store.dispatch(MediaLibraryAction.SelectFiles, {})) as Record<
                string,
                MediaItem
            >;

            if (!selectedFiles) {
                return;
            }

            // 2. check if selection contains a file named default.xxx.
            const defaultImage = Object.values(selectedFiles).filter(
                file => file.name.replace(/\.(.+)$/, "") === "default"
            );

            const lastDefaultImage = defaultImage.pop();

            // 3. If It does use the file values to preset update values
            const defaultUpdate = lastDefaultImage
                ? {
                      value: lastDefaultImage.id,
                      mediaItem: lastDefaultImage
                  }
                : null;

            // 4. go through the selected files, if we find a file with a name that is matching this template's sizes
            // ( ${ cur.width }x${ cur.height } pattern - let's use this file for update.
            Object.keys(this.masterTemplateSizes).forEach(templateId => {
                let deliverableUpdate: { value?: string; mediaItem?: MediaItem } = {};

                if (defaultUpdate) {
                    deliverableUpdate = { ...defaultUpdate, ...deliverableUpdate };
                }

                const matchingSize = Object.values(selectedFiles).find(file =>
                    file.name.toLowerCase().includes(this.masterTemplateSizes[templateId])
                );

                if (matchingSize) {
                    deliverableUpdate.value = matchingSize.id;
                    deliverableUpdate.mediaItem = matchingSize;
                }

                if (!defaultUpdate) {
                    return;
                }

                this.update({
                    editable: this.editable,
                    updateData: defaultUpdate,
                    groupsAffectingEditable: this.groupsAffectingEditable,
                    templateId
                });
            });
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.editable-image-item .widget-mode {
    color: $cmp-dark-disabled-color;
}

.editable-image-item .widget-mode--active {
    color: $cmp-dark-font-color;
}
</style>
